import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import './RecommendedTechs.css';

// Default data - can be overridden via props
const DEFAULT_TECHS = [
  {
    id: 'tech-001', // Unique ID instead of index
    name: 'Peter',
    phone: '516-426-2254',
    description:
      'Our trusted Harley Davidson® technician. With years of experience, Peter is your go-to expert for all your motorcycle maintenance and repair needs.',
    email: '',
    specialty: ' Systems',
  },
];

const DEFAULT_CONFIG = {
  title: 'Recommended Techs',
  subtitle: 'Our Trusted Technicians',
  description:
    'While we are no longer operating our shop, here are some trusted technicians we recommend for various services.',
};  

/**
 * TechCard - Individual tech card component
 * Memoized for performance optimization
 */
const TechCard = memo(({ tech }) => {
  // Only render contact info if it exists
  const hasContactInfo = tech.phone || tech.email;

  return (
    <article
      className="tech-card"
      role="region"
      aria-label={`${tech.name}'s contact information`}
    >
      <h3 className="tech-card-name">{tech.name}</h3>

      {tech.description && (
        <p className="tech-card-description">{tech.description}</p>
      )}

      {hasContactInfo && (
        <div className="tech-card-contact">
          {tech.phone && (
            <p className="tech-card-phone">
              <span className="label">Phone:</span>
              <a href={`tel:${tech.phone}`} aria-label={`Call ${tech.name}`}>
                {tech.phone}
              </a>
            </p>
          )}
          {tech.email && (
            <p className="tech-card-email">
              <span className="label">Email:</span>
              <a
                href={`mailto:${tech.email}`}
                aria-label={`Email ${tech.name}`}
              >
                {tech.email}
              </a>
            </p>
          )}
        </div>
      )}
    </article>
  );
});

TechCard.displayName = 'TechCard';

TechCard.propTypes = {
  tech: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    phone: PropTypes.string,
    email: PropTypes.string,
    specialty: PropTypes.string,
  }).isRequired,
};

/**
 * RecommendedTechs Component
 * Displays a list of recommended technicians with contact information
 *
 * @param {Object} props - Component props
 * @param {Array} props.techs - Array of tech objects
 * @param {string} props.title - Main title
 * @param {string} props.subtitle - Subtitle
 * @param {string} props.description - Description text
 * @returns {React.ReactElement}
 */
const RecommendedTechs = ({
  techs = DEFAULT_TECHS,
  title = DEFAULT_CONFIG.title,
  subtitle = DEFAULT_CONFIG.subtitle,
  description = DEFAULT_CONFIG.description,
}) => {
  // Validate and filter techs
  const validTechs = useMemo(() => {
    if (!Array.isArray(techs) || techs.length === 0) {
      return [];
    }
    return techs.filter((tech) => tech && tech.id && tech.name);
  }, [techs]);

  // Handle empty state
  if (validTechs.length === 0) {
    return (
      <section className="recommended-techs-container">
        <h1 className="recommended-techs-title">{title}</h1>
        <p className="recommended-techs-empty" role="status">
          No technicians available at this time.
        </p>
      </section>
    );
  }

  return (
    <section className="recommended-techs-container">
      {/* Header */}
      <header className="recommended-techs-header">
        <h1 className="recommended-techs-title">{title}</h1>
      </header>

      {/* Subheader and Description */}
      <div className="recommended-techs-intro">
        <h2 className="recommended-techs-subtitle">{subtitle}</h2>
        <p className="recommended-techs-description">{description}</p>
      </div>

      {/* Tech Cards Grid */}
      <div
        className="tech-cards-grid"
        role="region"
        aria-label="Recommended technicians"
      >
        {validTechs.map((tech) => (
          <TechCard key={tech.id} tech={tech} />
        ))}
      </div>
    </section>
  );
};

RecommendedTechs.propTypes = {
  techs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      phone: PropTypes.string,
      email: PropTypes.string,
      specialty: PropTypes.string,
    }),
  ),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
};

RecommendedTechs.defaultProps = {
  techs: DEFAULT_TECHS,
  title: DEFAULT_CONFIG.title,
  subtitle: DEFAULT_CONFIG.subtitle,
  description: DEFAULT_CONFIG.description,
};

export default memo(RecommendedTechs);
